import { CITIES } from '@/config/cities';
import { RACER_POOL, VEHICLE_UNLOCKS, vehicleUnlockedBy } from '@/config/racers';
import type { SaveData, TourResult } from '@/core/SaveData';

/**
 * Progression.ts — pure championship logic for the Tour (owner-approved model,
 * 2026-06-11): every race awards points by finish place; trophies are
 * gold/silver/bronze for 1st/2nd/3rd; the championship table ranks every racer
 * who took part across the Tour so the end-of-tour summary is a real title
 * fight. M14: fields can be 4–8 racers, so the points table runs to 8th.
 */

/** Championship points by place (index 1..8; 0 unused). Max tour total: 90. */
export const POINTS_BY_PLACE = [0, 10, 6, 4, 3, 2, 1, 1, 1];

export type Trophy = 'gold' | 'silver' | 'bronze' | 'none';

export function pointsForPlace(place: number): number {
  return POINTS_BY_PLACE[place] ?? 0;
}

export function trophyForPlace(place: number): Trophy {
  if (place === 1) return 'gold';
  if (place === 2) return 'silver';
  if (place === 3) return 'bronze';
  return 'none';
}

export interface ChampionshipRow {
  racerId: number;
  name: string;
  color: number;
  isPlayer: boolean;
  points: number;
  wins: number;
}

/**
 * The championship table for a set of tour results, best first. Covers every
 * pool racer who actually took part (fields can change size mid-tour).
 * Ties break by total points, then race wins, then 2nd places; a still-perfect
 * tie ranks the player first (friendly, and deterministic).
 */
export function championship(results: TourResult[]): ChampionshipRow[] {
  const rows: ChampionshipRow[] = RACER_POOL.map((r) => ({
    racerId: r.id,
    name: r.name,
    color: r.color,
    isPlayer: r.isPlayer,
    points: 0,
    wins: 0,
  }));
  const seconds = RACER_POOL.map(() => 0);
  const raced = new Set<number>([0]); // the player always shows
  for (const res of results) {
    for (const row of rows) {
      const place = res.places[row.racerId] ?? 0;
      if (place > 0) raced.add(row.racerId);
      row.points += pointsForPlace(place);
      if (place === 1) row.wins += 1;
      if (place === 2) seconds[row.racerId] += 1;
    }
  }
  return rows
    .filter((r) => raced.has(r.racerId))
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (seconds[b.racerId] !== seconds[a.racerId]) return seconds[b.racerId] - seconds[a.racerId];
      return (b.isPlayer ? 1 : 0) - (a.isPlayer ? 1 : 0);
    });
}

/** Player's trophy for each tour city raced so far, in tour order. */
export function tourTrophies(results: TourResult[]): Trophy[] {
  return results.map((r) => trophyForPlace(r.places[0] ?? 0));
}

/** The player's best trophy for a city across all play (from the save record). */
export function cityTrophy(save: SaveData, cityId: string): Trophy {
  const rec = save.cities[cityId];
  return rec ? trophyForPlace(rec.bestPlace) : 'none';
}

/** True when the player tops the championship table. */
export function playerIsChampion(results: TourResult[]): boolean {
  const table = championship(results);
  return table.length > 0 && table[0].isPlayer;
}

/** Total cities in the Tour (exported so UI never hardcodes 9). */
export function tourLength(): number {
  return CITIES.length;
}

// ---------------------------------------------------------------------------
// M62 — vehicle unlocks (lifetime points). See config VEHICLE_UNLOCKS.
// ---------------------------------------------------------------------------

/**
 * Is a vehicle available to pick? The starter 'coupe' is always free; the
 * earnable cars unlock once lifetime points reach their VEHICLE_UNLOCKS
 * threshold OR once they're in the save's granted-forever `unlockedVehicles`
 * set (M114 — past crossings + the legacy migration, so a threshold retune
 * never re-locks an earned car). The real rule lives in config
 * `vehicleUnlockedBy` (shared with the SaveData load-boundary ride coercion,
 * cycle-free). Fail-open for unknown ids so a bad id can never soft-brick
 * selection. Mirrors `isCityUnlocked`'s "first is always open".
 */
export function isVehicleUnlocked(save: SaveData, id: string): boolean {
  return vehicleUnlockedBy(save.lifetimePoints, save.unlockedVehicles, id);
}

// M78: awarding moved to core/skillPoints.ts (applySkillAward) — the one
// choke point for the Skill-Point economy (podium bonuses + the future
// Supabase ledger contract). This module keeps the DERIVED reads.

/**
 * The next vehicle a Skill-Point total will unlock and how many points away it
 * is, or null once everything is unlocked. Reads the ascending VEHICLE_UNLOCKS
 * ladder. (M78: points-shaped so the results screen can ask about a mid-award
 * total, not just the saved one. M114: pass the save's granted set so a
 * legacy-granted car is never advertised as the "next" unlock — callers
 * without a save in hand may omit it and get the pure points walk.)
 */
export function nextUnlockAt(
  points: number,
  granted: readonly string[] = [],
): { id: string; pointsToGo: number } | null {
  const up = VEHICLE_UNLOCKS.find((u) => points < u.points && !granted.includes(u.id));
  return up ? { id: up.id, pointsToGo: up.points - points } : null;
}

/** The Garage progress line's view of {@link nextUnlockAt} (saved total). */
export function nextUnlock(save: SaveData): { id: string; pointsToGo: number } | null {
  return nextUnlockAt(save.lifetimePoints, save.unlockedVehicles);
}
